import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Clock, 
  Wrench,
  DollarSign, 
  CheckCircle, 
  AlertCircle,
  FileText,
  User,
  Building,
  Upload,
  MessageSquare,
  Timer,
  AlertTriangle,
  ChevronDown,
  Image as ImageIcon,
  Paperclip,
  MoreVertical,
  CheckCircle2,
  XCircle,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  Calendar,
  Users
} from 'lucide-react';
import BackButton from '../../../components/BackButton';
import { QRCode } from 'react-qr-code';
import { motion } from 'framer-motion';
import { PDFGenerator } from '../../../utils/PDFGenerator'; // Adjust path as needed

const TicketDetail = () => {
  const { id } = useParams();
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Add new state for action modal
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [actionFiles, setActionFiles] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState({
    amount: '',
    date: '',
    vendor: '',
    invoiceNumber: '',
    description: ''
  });
  
  // Add new state for report modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportId, setReportId] = useState('');
  
  // Mock user data - replace with actual user context
  const currentUser = {
    id: 'USER123',
    role: 'MAINTENANCE_MANAGER',
    name: 'John Manager'
  };
  
  // Mock data structure aligned with typical maintenance ticket form fields
  const [ticket, setTicket] = useState({
    id,
    // Basic Information
    title: "AC Unit Malfunction",
    requestType: "repair", // repair, maintenance, installation, inspection
    priority: "high",
    status: "in-progress",
    
    // Location Details
    facility: "Store #123",
    building: "Main Building",
    floor: "1st Floor",
    area: "Dining Area",
    roomNumber: "DR-101",
    
    // Asset Information
    asset: {
      id: "AC-123",
      name: "Carrier AC Unit",
      type: "HVAC",
      model: "XYZ-789",
      serialNumber: "CAR123456789",
      manufacturer: "Carrier",
      location: "Roof Section A",
      installationDate: "2022-01-15",
      warrantyStatus: "Valid until 2025",
      maintenanceHistory: [
        {
          date: "2024-01-15",
          type: "Preventive",
          technician: "Mike Tech",
          description: "Quarterly maintenance"
        }
      ]
    },

    // Problem Details
    description: "Main dining area AC not cooling properly...",
    symptoms: [
      "Insufficient cooling",
      "Unusual noise",
      "High energy consumption"
    ],
    impactLevel: "high", // high, medium, low
    affectedUsers: "Staff and Customers",
    
    // Schedule & Timing
    submittedDate: "2024-03-15T09:30:00",
    requestedDate: "2024-03-16",
    preferredTimeSlot: "morning",
    dueDate: "2024-03-18",
    
    // Assignment & Cost
    assignedTo: {
      id: "TECH-001",
      name: "Mike Tech",
      role: "HVAC Specialist",
      contact: "555-0123"
    },
    estimatedDuration: "4 hours",
    estimatedCost: 1500,
    actualCost: 1200,
    budgetCode: "MAINT-2024-Q1",
    
    // Safety & Compliance
    safetyRequirements: [
      "Roof access required",
      "Height safety equipment needed"
    ],
    permits: [
      {
        type: "Work at Height",
        status: "Approved",
        expiryDate: "2024-03-20"
      }
    ],
    
    // Additional Information
    attachments: [
      {
        id: 1,
        name: "Initial_Inspection.pdf",
        type: "document",
        size: "2.4 MB",
        uploadedBy: "John Doe",
        uploadedAt: "2024-03-15T09:30:00"
      }
    ],
    notes: "Previous maintenance history shows recurring issues",
    
    // Tracking & Progress
    progress: 65,
    sla: {
      responseTime: "4 hours",
      resolutionTime: "24 hours",
      status: "Within SLA"
    },
    
    // Workflow History
    timeline: [
      {
        date: "2024-03-15T09:30:00",
        type: "submission",
        user: "John Doe",
        action: "Ticket submitted",
        details: "Initial request created"
      }
    ],
    
    // Related Items
    relatedTickets: ["MT-456", "MT-789"],
    parentTicket: null,
    
    // Custom Fields
    customFields: {
      environmentalImpact: "Medium",
      energyEfficiencyRating: "B",
      maintenanceCycle: "Quarterly"
    },

    // Additional fields to match form
    department: "Operations",
    subLocation: "Kitchen Area",
    problemType: "Equipment Malfunction",
    subProblemType: "Cooling System",
    
    // Asset QR Code
    assetQRCode: "ASSET-123-456",
    
    // Images and Documents
    images: [
      {
        id: 1,
        url: "/path/to/image1.jpg",
        thumbnail: "/path/to/thumbnail1.jpg",
        caption: "Initial damage photo",
        uploadedBy: "John Doe",
        uploadedAt: "2024-03-15T09:30:00"
      }
    ],
    documents: [
      {
        id: 1,
        type: "invoice",
        name: "Parts_Invoice.pdf",
        url: "/path/to/invoice.pdf",
        uploadedBy: "Mike Tech",
        uploadedAt: "2024-03-16T14:30:00"
      }
    ]
  });

  // Get available actions based on user role and ticket status
  const getAvailableActions = () => {
    const actions = [];
    
    switch(currentUser.role) {
      case 'MAINTENANCE_MANAGER':
        actions.push(
          { label: 'Approve', icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Reject', icon: XCircle, color: 'text-red-600' },
          { label: 'Reassign', icon: RotateCcw, color: 'text-blue-600' },
          { label: 'Put On Hold', icon: PauseCircle, color: 'text-amber-600' },
          { label: 'Mark Completed', icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Upload Image', icon: ImageIcon, color: 'text-blue-600' },
          { label: 'Upload Invoice', icon: FileText, color: 'text-purple-600' }
        );
        break;
      case 'MAINTENANCE_STAFF':
        actions.push(
          { label: 'Start Work', icon: PlayCircle, color: 'text-blue-600' },
          { label: 'Update Progress', icon: Timer, color: 'text-blue-600' },
          { label: 'Request Review', icon: AlertCircle, color: 'text-amber-600' },
          { label: 'Mark Completed', icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Upload Image', icon: ImageIcon, color: 'text-blue-600' },
          { label: 'Upload Invoice', icon: FileText, color: 'text-purple-600' }
        );
        break;
      default:
        actions.push(
          { label: 'Request Update', icon: MessageSquare, color: 'text-blue-600' },
          { label: 'Upload Image', icon: ImageIcon, color: 'text-blue-600' }
        );
    }
    
    return actions;
  };

  const handleFileSelect = (event) => {
    setSelectedFiles([...event.target.files]);
  };

  // Handle action click
  const handleActionClick = (action) => {
    setSelectedAction(action);
    setShowActionModal(true);
    setShowActionMenu(false);
  };

  // Handle action submit
  const handleActionSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Mock action submission based on action type
      switch(selectedAction.label) {
        case 'Upload Image':
          console.log('Uploading images:', {
            files: actionFiles,
            comment: actionComment
          });
          // Here you would typically call your API to upload images
          break;
        
        case 'Upload Invoice':
          console.log('Uploading invoice:', {
            files: actionFiles,
            invoiceDetails,
            comment: actionComment
          });
          // Here you would typically call your API to upload invoice
          break;
        
        default:
          console.log('Processing action:', {
            action: selectedAction.label,
            comment: actionComment,
            files: actionFiles
          });
          // Here you would typically call your API to process the action
      }

      // Mock successful submission
      // In real app, you'd update the ticket state after successful API call
      setTicket(prev => ({
        ...prev,
        timeline: [
          {
            date: new Date().toISOString(),
            type: 'update',
            user: currentUser.name,
            action: selectedAction.label,
            details: actionComment,
            attachments: Array.from(actionFiles).map(file => ({
              name: file.name,
              size: `${Math.round(file.size / 1024)} KB`
            }))
          },
          ...prev.timeline
        ]
      }));

      // Reset form
      setActionComment('');
      setActionFiles([]);
      setInvoiceDetails({
        amount: '',
        date: '',
        vendor: '',
        invoiceNumber: '',
        description: ''
      });
      setShowActionModal(false);

      // Show success message (you might want to add a toast notification here)
      alert(`${selectedAction.label} completed successfully`);

    } catch (error) {
      console.error('Error processing action:', error);
      alert('Error processing action. Please try again.');
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    // Implement comment submission
    console.log('Submitting comment:', newComment);
    setNewComment('');
    setShowCommentInput(false);
  };

  // Update workflow steps to be more comprehensive
  const workflowSteps = [
    { 
      status: 'completed', 
      label: 'Submitted',
      date: '15 Mar, 9:30 AM',
      icon: FileText,
      user: 'John Manager',
      role: 'Store Manager'
    },
    { 
      status: 'completed', 
      label: 'Reviewed',
      date: '15 Mar, 10:00 AM',
      icon: AlertTriangle,
      user: 'Mike Supervisor',
      role: 'Maintenance Supervisor'
    },
    { 
      status: 'completed', 
      label: 'Approved',
      date: '15 Mar, 10:15 AM',
      icon: CheckCircle2,
      user: 'Sarah Admin',
      role: 'Regional Admin'
    },
    { 
      status: 'completed', 
      label: 'Assigned',
      date: '15 Mar, 11:00 AM',
      icon: Users,
      user: 'Mike Supervisor',
      role: 'Maintenance Supervisor'
    },
    { 
      status: 'current', 
      label: 'In Progress',
      date: '15 Mar, 2:00 PM',
      icon: Timer,
      user: 'Mike Tech',
      role: 'Technician'
    },
    { 
      status: 'pending', 
      label: 'Work Done',
      date: 'Pending',
      icon: CheckCircle2
    },
    { 
      status: 'pending', 
      label: 'QA Check',
      date: 'Pending',
      icon: AlertTriangle
    },
    { 
      status: 'pending', 
      label: 'Verification',
      date: 'Pending',
      icon: CheckCircle2
    },
    { 
      status: 'pending', 
      label: 'Closed',
      date: 'Pending',
      icon: CheckCircle2
    }
  ];

  // Add function to generate unique report ID
  const generateReportId = () => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 1000);
    return `RPT-${timestamp}-${random}`;
  };

  // Add function to generate report
  const handleGenerateReport = async () => {
    const newReportId = generateReportId();
    setReportId(newReportId);

    // Prepare report data
    const reportData = {
      reportId: newReportId,
      ticket: {
        id: ticket.id,
        title: ticket.title,
        status: ticket.status,
        priority: ticket.priority,
        submittedDate: ticket.submittedDate,
      },
      asset: {
        ...ticket.asset,
        maintenanceHistory: ticket.asset.maintenanceHistory,
        totalServices: ticket.asset.maintenanceHistory.length,
      },
      maintenance: {
        type: ticket.requestType,
        description: ticket.description,
        symptoms: ticket.symptoms,
        impactLevel: ticket.impactLevel,
        assignedTo: ticket.assignedTo,
        timeline: ticket.timeline,
      },
      costs: {
        estimated: ticket.estimatedCost,
        actual: ticket.actualCost,
        invoices: ticket.documents.filter(doc => doc.type === 'invoice'),
      },
      location: {
        facility: ticket.facility,
        building: ticket.building,
        floor: ticket.floor,
        area: ticket.area,
      },
      safety: {
        requirements: ticket.safetyRequirements,
        permits: ticket.permits,
      }
    };

    try {
      // Generate PDF using your existing PDFGenerator
      const pdfBlob = await PDFGenerator.generateMaintenanceReport(reportData);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Maintenance_Report_${reportData.reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Update ticket with report reference
      setTicket(prev => ({
        ...prev,
        reports: [...(prev.reports || []), {
          id: reportData.reportId,
          generatedAt: new Date().toISOString(),
          type: 'maintenance',
        }]
      }));

      // Show success message
      alert('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      <BackButton className="mb-4" />
      
      {/* Header Section - Changed from gradient to solid color */}
      <div className="bg-blue-600 rounded-xl shadow-lg mb-6">
        <div className="p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{ticket.title}</h1>
                <span className="text-blue-200">#{ticket.id}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${ticket.priority === 'high' 
                    ? 'bg-red-500 text-white' 
                    : ticket.priority === 'medium'
                    ? 'bg-amber-500 text-white'
                    : 'bg-green-500 text-white'}`}>
                  {ticket.priority.toUpperCase()}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${ticket.status === 'completed' 
                    ? 'bg-green-500 text-white' 
                    : ticket.status === 'in-progress'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-500 text-white'}`}>
                  {ticket.status.toUpperCase()}
                </span>
                <span className="text-xs text-blue-200">
                  Created {new Date(ticket.submittedDate).toLocaleDateString()}
                </span>
              </div>
            </div>
            
            {/* Action Button with Fixed Position Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowActionMenu(!showActionMenu)}
                className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 
                         shadow-sm hover:shadow transition-all flex items-center gap-2"
              >
                Take Action
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {/* Action Menu with higher z-index */}
              {showActionMenu && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white shadow-xl border border-gray-200 py-1 z-50">
                  {getAvailableActions().map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleActionClick(action)}
                      className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <action.icon className={`w-4 h-4 ${action.color}`} />
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Workflow Steps - Fixed Alignment */}
          <div className="mt-8 mb-12">
            {/* Add a lighter background container */}
            <div className="bg-blue-500/10 rounded-xl p-6 relative">
              {/* Background Line */}
              <div className="absolute left-0 right-0 top-[50%] h-[2px] bg-blue-400/30" />
              
              <div className="relative flex justify-between">
                {workflowSteps.map((step, index, array) => {
                  const Icon = step.icon;
                  const isLast = index === array.length - 1;
                  
                  return (
                    <div key={index} className="relative flex-1 flex flex-col items-center">
                      {/* Progress Line */}
                      {!isLast && (
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          className={`absolute left-1/2 w-full h-[2px] top-[20px]
                            ${step.status === 'completed' ? 'bg-green-500' : 'bg-transparent'}`} 
                        />
                      )}
                      
                      {/* Step Circle with Animation */}
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ 
                          type: "spring",
                          stiffness: 260,
                          damping: 20,
                          delay: index * 0.1 
                        }}
                        className="relative z-10"
                      >
                        <div className={`relative w-10 h-10 rounded-lg flex items-center justify-center
                          ${step.status === 'completed'
                            ? 'bg-green-500 shadow-lg'
                            : step.status === 'current'
                            ? 'bg-blue-500 shadow-lg'
                            : 'bg-blue-400/30'
                          }`}
                        >
                          <Icon className="w-4 h-4 text-white" />

                          {/* Pulse Effect for Current Step */}
                          {step.status === 'current' && (
                            <div className="absolute -inset-1 rounded-lg">
                              <motion.div
                                animate={{
                                  scale: [1, 1.2, 1.2],
                                  opacity: [0.4, 0, 0]
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  ease: ["easeOut", "linear"],
                                  times: [0, 0.3, 1]
                                }}
                                className="absolute inset-0 rounded-lg bg-blue-500/40"
                              />
                            </div>
                          )}
                        </div>

                        {/* Step Info - Updated text colors for better visibility */}
                        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-28 text-center">
                          <p className="text-xs font-medium mb-1 text-gray-100">
                            {step.label}
                          </p>
                          {step.user && (
                            <div className="flex flex-col items-center gap-0.5">
                              <p className="text-[10px] text-white/90 font-medium">
                                {step.user}
                              </p>
                              <p className="text-[9px] text-white/80">
                                {step.date}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="col-span-3 space-y-6">
          {/* Top Cards Grid */}
          <div className="grid grid-cols-3 gap-6">
            {/* Priority & Status Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      ticket.status === 'in-progress' ? 'bg-blue-500 animate-pulse' :
                      ticket.status === 'completed' ? 'bg-green-500' :
                      'bg-amber-500'
                    }`} />
                    <span className="text-lg font-semibold text-gray-900">
                      {ticket.status.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${
                      ticket.priority === 'high' ? 'text-red-500' :
                      ticket.priority === 'medium' ? 'text-amber-500' :
                      'text-green-500'
                    }`} />
                    <span className="text-sm text-gray-600">{ticket.priority} priority</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${
                  ticket.status === 'in-progress' ? 'bg-blue-50' :
                  ticket.status === 'completed' ? 'bg-green-50' :
                  'bg-amber-50'
                }`}>
                  <Timer className={`w-5 h-5 ${
                    ticket.status === 'in-progress' ? 'text-blue-600' :
                    ticket.status === 'completed' ? 'text-green-600' :
                    'text-amber-600'
                  }`} />
                </div>
              </div>
            </div>

            {/* SLA Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500">SLA Status</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">{ticket.sla.status}</p>
                  <div className="mt-1 text-sm text-gray-600">
                    Response: {ticket.sla.responseTime}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-green-50">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </div>

            {/* Assignment Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500">Assigned To</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">{ticket.assignedTo.name}</p>
                  <p className="mt-1 text-sm text-gray-600">{ticket.assignedTo.role}</p>
                </div>
                <div className="p-3 rounded-lg bg-violet-50">
                  <User className="w-5 h-5 text-violet-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Asset Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Asset Information</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Asset ID:</span>
                  <span className="text-sm font-medium text-gray-900">{ticket.asset.id}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-6">
                {/* QR Code */}
                <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl border border-gray-100">
                  <QRCode value={ticket.assetQRCode} size={120} />
                  <p className="mt-3 text-sm text-gray-600">Scan for asset details</p>
                </div>

                {/* Asset Details */}
                <div className="col-span-2 grid grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Equipment</p>
                    <p className="mt-1 font-medium text-gray-900">{ticket.asset.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Model</p>
                    <p className="mt-1 font-medium text-gray-900">{ticket.asset.model}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Manufacturer</p>
                    <p className="mt-1 font-medium text-gray-900">{ticket.asset.manufacturer}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Serial Number</p>
                    <p className="mt-1 font-medium text-gray-900">{ticket.asset.serialNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Installation Date</p>
                    <p className="mt-1 font-medium text-gray-900">{ticket.asset.installationDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Warranty Status</p>
                    <p className="mt-1 font-medium text-green-600">{ticket.asset.warrantyStatus}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Location & Problem Details */}
          <div className="grid grid-cols-2 gap-6">
            {/* Location Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Building className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Facility</span>
                    <span className="text-sm font-medium text-gray-900">{ticket.facility}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Building</span>
                    <span className="text-sm font-medium text-gray-900">{ticket.building}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Floor</span>
                    <span className="text-sm font-medium text-gray-900">{ticket.floor}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-500">Area</span>
                    <span className="text-sm font-medium text-gray-900">{ticket.area}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Problem Details Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-red-50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Problem Details</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-900">{ticket.description}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Symptoms</p>
                    <div className="flex flex-wrap gap-2">
                      {ticket.symptoms.map((symptom, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-gray-50 rounded-full text-xs text-gray-600"
                        >
                          {symptom}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-2 border-t border-gray-100">
                    <span className="text-sm text-gray-500">Impact Level</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium
                      ${ticket.impactLevel === 'high' ? 'bg-red-100 text-red-700' :
                        ticket.impactLevel === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'}`}>
                      {ticket.impactLevel.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Images & Attachments</h3>
              <div className="grid grid-cols-4 gap-4">
                {ticket.images.map((image) => (
                  <div 
                    key={image.id}
                    className="relative group cursor-pointer"
                    onClick={() => {
                      setSelectedImage(image);
                      setShowGallery(true);
                    }}
                  >
                    <img 
                      src={image.thumbnail} 
                      alt={image.caption}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 
                                  opacity-0 group-hover:opacity-100 transition-opacity
                                  rounded-lg flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Timeline</h3>
              <div className="space-y-6">
                {ticket.timeline.map((event, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center
                        ${event.type === 'status' ? 'bg-blue-100' :
                          event.type === 'approval' ? 'bg-green-100' :
                          event.type === 'assignment' ? 'bg-purple-100' :
                          'bg-gray-100'}`}>
                        {event.type === 'status' && <Clock className="w-4 h-4 text-blue-600" />}
                        {event.type === 'approval' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {event.type === 'assignment' && <User className="w-4 h-4 text-purple-600" />}
                        {event.type === 'update' && <MessageSquare className="w-4 h-4 text-gray-600" />}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">{event.user}</p>
                        <span className="text-sm text-gray-500">{event.date}</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">{event.details}</p>
                      {event.attachments && event.attachments.length > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          {event.attachments.map((attachment, idx) => (
                            <a
                              key={idx}
                              href="#"
                              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 rounded text-xs text-gray-600 hover:bg-gray-100"
                            >
                              <FileText className="w-3 h-3" />
                              {attachment.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Side Menu - Update the Upload Section */}
        <div className="space-y-6">
          {/* Upload Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => handleActionClick({
                    label: 'Upload Image',
                    icon: ImageIcon,
                    color: 'text-blue-600'
                  })}
                  className="w-full px-4 py-2 text-sm text-blue-600 border border-blue-200 
                             hover:bg-blue-50 rounded-lg flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Upload Image
                </button>
                <button 
                  onClick={() => handleActionClick({
                    label: 'Upload Invoice',
                    icon: FileText,
                    color: 'text-purple-600'
                  })}
                  className="w-full px-4 py-2 text-sm text-purple-600 border border-purple-200 
                             hover:bg-purple-50 rounded-lg flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Upload Invoice
                </button>
                <button 
                  onClick={handleGenerateReport}
                  className="w-full px-4 py-2 text-sm text-green-600 border border-green-200 
                             hover:bg-green-50 rounded-lg flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Generate Report
                </button>
              </div>
            </div>
          </div>

          {/* Reports Section - Add this new section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Generated Reports</h3>
              <div className="space-y-2">
                {ticket.reports?.map((report) => (
                  <div
                    key={report.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg"
                  >
                    <FileText className="w-4 h-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        Report {report.id}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(report.generatedAt).toLocaleString()}
                      </p>
                    </div>
                    <QRCode
                      value={report.id}
                      size={32}
                      className="hidden group-hover:block"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Documents List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Documents</h3>
              <div className="space-y-2">
                {ticket.documents.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg"
                  >
                    <FileText className="w-4 h-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                      <p className="text-xs text-gray-500">{doc.type}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      {showGallery && selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="max-w-4xl w-full p-4">
            <img 
              src={selectedImage.url} 
              alt={selectedImage.caption}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            <p className="text-white text-center mt-4">{selectedImage.caption}</p>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedAction.color.includes('green') ? 'bg-green-50' :
                    selectedAction.color.includes('red') ? 'bg-red-50' :
                    selectedAction.color.includes('amber') ? 'bg-amber-50' :
                    'bg-blue-50'
                  }`}>
                    <selectedAction.icon className={`w-5 h-5 ${selectedAction.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedAction.label}
                  </h3>
                </div>
                <button 
                  onClick={() => setShowActionModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleActionSubmit} className="p-6 space-y-6">
              {/* Invoice Details - Only show for invoice upload */}
              {selectedAction.label === 'Upload Invoice' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount
                      </label>
                      <input
                        type="number"
                        value={invoiceDetails.amount}
                        onChange={(e) => setInvoiceDetails(prev => ({
                          ...prev,
                          amount: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter amount"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={invoiceDetails.date}
                        onChange={(e) => setInvoiceDetails(prev => ({
                          ...prev,
                          date: e.target.value
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor
                    </label>
                    <input
                      type="text"
                      value={invoiceDetails.vendor}
                      onChange={(e) => setInvoiceDetails(prev => ({
                        ...prev,
                        vendor: e.target.value
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter vendor name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      value={invoiceDetails.invoiceNumber}
                      onChange={(e) => setInvoiceDetails(prev => ({
                        ...prev,
                        invoiceNumber: e.target.value
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter invoice number"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Comment Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedAction.label === 'Upload Invoice' ? 'Additional Details' : 'Comments'}
                </label>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add your comments..."
                  required
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attachments
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 
                              border-dashed rounded-lg hover:border-blue-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer rounded-md font-medium 
                                      text-blue-600 hover:text-blue-500">
                        <span>Upload files</span>
                        <input
                          type="file"
                          multiple
                          className="sr-only"
                          onChange={(e) => setActionFiles([...e.target.files])}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                </div>
                {/* File List */}
                {actionFiles.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {Array.from(actionFiles).map((file, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <FileText className="w-4 h-4" />
                        <span>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setActionFiles(prev => 
                            Array.from(prev).filter((_, i) => i !== index)
                          )}
                          className="ml-auto text-red-500 hover:text-red-600"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowActionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 
                           border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                           hover:bg-blue-700 rounded-lg"
                >
                  Confirm {selectedAction.label}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail; 